import './chat-message-files-preview-modal.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { ChatMessageItem } from '../../../../../data/data/types';
import {
  FileTypeImage,
  formatMimeType,
  getBase64FromServer,
  isValidFileType,
  onHandleDownloadFile,
} from '../../../../../data/utils/upload.utils';
import { Button, ButtonIcon } from '../../../../button/button';
import { Modal, ModalWidth } from '../../../../modal/modal';
import { Spinner } from '../../../../spinner/spinner';
import { ChatMessageFileDeletePrompt } from '../chat-message-file-delete-prompt/chat-message-file-delete-prompt';

type ChatMessageFilesPreviewModalProps = {
  messageItem: ChatMessageItem;
  slide?: number;
  onClose?: () => void;
};

export function ChatMessageFilesPreviewModal({
  messageItem,
  onClose,
  ...props
}: ChatMessageFilesPreviewModalProps) {
  const [slide, setSlide] = useState<number>(0);
  const [slideAnimation, setSlideAnimation] = useState<boolean>(false);

  const [previews, setPreviews] = useState<{ [key: string]: string }>({});

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const onLoadPreview = useCallback(
    (id: string, url: string | null | undefined) => {
      if (url) {
        getBase64FromServer(url).then((base64: string) => {
          setPreviews(prevState => ({
            ...prevState,
            [id]: base64,
          }));
        });

        return true;
      }

      return false;
    },
    []
  );

  const files = useMemo(
    () =>
      (messageItem.files ?? []).map(option => {
        const id = uuid();

        return {
          id,
          name: option.title,
          file: option.file,
          type: formatMimeType(option.file),
          isImage: isValidFileType(formatMimeType(option.file), FileTypeImage),
          preview: onLoadPreview(id, option.file),
        };
      }),
    [messageItem.files, onLoadPreview]
  );

  const { prevButtonDisabled, nextButtonDisabled, paginationDisabled } =
    useMemo(
      () => ({
        prevButtonDisabled: slide < 1,
        nextButtonDisabled: slide >= files.length - 1,
        paginationDisabled: files.length <= 1,
      }),
      [slide, files]
    );

  useEffect(() => {
    if (props.slide) {
      if (props.slide <= files.length - 1) {
        setSlide(props.slide);
      }
    }
  }, [props.slide, files]);

  useEffect(() => {
    if (files.length > 0) {
      if (slide > files.length - 1) {
        setSlide(0);
      }
    } else {
      onClose?.();
    }
  }, [slide, files, onClose]);

  const onHandleSlide = useCallback((value: number) => {
    setSlide(() => {
      setSlideAnimation(true);

      return value;
    });
  }, []);

  const onHandlePrevSlide = useCallback(() => {
    let value = slide - 1;

    if (value < 0) {
      value = files.length - 1;
    }

    setSlide(() => {
      setSlideAnimation(true);

      return value;
    });
  }, [slide, files]);

  const onHandleNextSlide = useCallback(() => {
    let value = slide + 1;

    if (value > files.length - 1) {
      value = 0;
    }

    setSlide(() => {
      setSlideAnimation(true);

      return value;
    });
  }, [slide, files]);

  const onHandleDownload = useCallback(() => {
    const file = files[slide];

    if (file) {
      onHandleDownloadFile(file.file, file.name);
    }
  }, [files, slide]);

  const onNavigateSlides = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'ArrowLeft') {
        onHandlePrevSlide();
      } else if (e.code === 'ArrowRight') {
        onHandleNextSlide();
      }
    },
    [onHandlePrevSlide, onHandleNextSlide]
  );

  useEffect(() => {
    document.addEventListener('keyup', onNavigateSlides);

    return () => {
      document.removeEventListener('keyup', onNavigateSlides);
    };
  }, [onNavigateSlides]);

  return (
    <>
      <Modal
        width={ModalWidth.FULL}
        className="chat-message-file"
        onClose={onClose}
      >
        <div className="chat-message-file-preview-slider">
          <div
            className="chat-message-file-preview-slider__slider"
            style={{
              width: `${files.length * 100}%`,
              transform: `translate3d(-${
                (100 / files.length) * slide
              }%, 0px, 0px)`,
              transition: slideAnimation ? `all 0.25s linear` : undefined,
            }}
          >
            {files.map(option => {
              const preview = previews[option.id as keyof typeof previews];

              return (
                <div
                  key={option.id}
                  className="thumbnail"
                  style={{ width: `${100 / files.length}%` }}
                >
                  <span className={`preview ${option.type}`}>
                    {option.isImage &&
                      (!preview ? (
                        <Spinner />
                      ) : (
                        <img src={preview} alt={option.name ?? ''} />
                      ))}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="chat-message-file-preview-slider__controls">
            <button
              type="button"
              disabled={prevButtonDisabled}
              onClick={onHandlePrevSlide}
            >
              <i className="icon chevron-left" />
            </button>
            <button
              type="button"
              disabled={nextButtonDisabled}
              onClick={onHandleNextSlide}
            >
              <i className="icon chevron-right" />
            </button>
          </div>

          {!paginationDisabled && (
            <div className="chat-message-file-preview-slider__pagination">
              {files.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  className={slide === index ? 'active' : ''}
                  onClick={() => onHandleSlide(index)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          )}

          <div className="chat-message-file-preview-slider__toolbar">
            <Button
              icon={ButtonIcon.DELETE}
              onClick={() => setDeleteModalOpen(true)}
            />
            <Button icon={ButtonIcon.DOWNLOAD} onClick={onHandleDownload} />
          </div>
        </div>
      </Modal>

      {deleteModalOpen && files[slide] && (
        <ChatMessageFileDeletePrompt
          messageItem={messageItem}
          file={files[slide].file}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </>
  );
}
